import { useMemo, useState } from 'react';
import {
  Box,
  Icon,
  InlineStack,
  Paragraph,
  PlainButton,
  Search,
  Table,
  TableTbody,
  TableTd,
  TableTr,
  Text,
} from '@customerio/pluma-components/react';
import {
  attributeSearchPlaceholder,
  attributesTrailerText,
  availableAttributeNames,
  filterAttributeNames,
  VISIBLE_ATTRIBUTE_LIMIT,
} from '../utils/available-attributes';
import type { IdentifiersConfig } from '../utils/identifiers';
import { AttributeTypeLabel } from './attribute-type-label';

type AvailableAttributesProps = {
  /** The attribute names the workspace has received. */
  sentAttributes: readonly string[];
  /** The environment holds more sent attributes than it lists. */
  moreAttributes: boolean;
  identifiers: IdentifiersConfig | null | undefined;
  /** The names already on the profile being edited. */
  currentAttributeKeys: readonly string[];
  isSaving: boolean;
  onAddAttribute: (attribute: string) => void;
};

/**
 * The workspace's attribute catalog, minus what the profile already has:
 * searchable, capped at 100 visible names, one click adds a name to the profile.
 */
export function AvailableAttributes({
  sentAttributes,
  moreAttributes,
  identifiers,
  currentAttributeKeys,
  isSaving,
  onAddAttribute,
}: AvailableAttributesProps) {
  const [search, setSearch] = useState('');

  const available = useMemo(
    () => availableAttributeNames(sentAttributes, identifiers, currentAttributeKeys),
    [sentAttributes, identifiers, currentAttributeKeys],
  );
  const filtered = useMemo(() => filterAttributeNames(available, search), [available, search]);
  const visible = filtered.slice(0, VISIBLE_ATTRIBUTE_LIMIT);
  const trailer = attributesTrailerText({
    visibleCount: visible.length,
    filteredCount: filtered.length,
    moreAttributes,
    allAvailableMatch: filtered.length === available.length,
    hasSearchTerm: search !== '',
  });

  return (
    <>
      <Box px="300" pb="300">
        <Search
          ariaLabel="Attribute search"
          placeholder={attributeSearchPlaceholder(moreAttributes)}
          value={search}
          onSearchInput={setSearch}
          onSearch={setSearch}
          shouldSearchOnInput
          data-test="available-attributes-search"
        />
      </Box>

      {filtered.length > 0 ? (
        <>
          <Table caption="Available attributes">
            <TableTbody>
              {visible.map((attribute) => (
                <TableTr key={attribute} shouldHighlightOnHover={!isSaving}>
                  <TableTd>
                    <PlainButton
                      onClick={() => onAddAttribute(attribute)}
                      isDisabled={isSaving}
                      data-test="available-attributes-row"
                    >
                      <InlineStack alignBlock="center" gap="100">
                        <Icon name="add" />
                        <Box as="span" truncate>
                          {attribute}
                        </Box>
                        <AttributeTypeLabel attribute={attribute} identifiers={identifiers} />
                      </InlineStack>
                    </PlainButton>
                  </TableTd>
                </TableTr>
              ))}
            </TableTbody>
          </Table>

          {trailer != null ? (
            <Text as="div" color="subtle" data-test="available-attributes-trailer">
              {trailer}
            </Text>
          ) : null}
        </>
      ) : (
        <Paragraph textAlign="center">No attributes available.</Paragraph>
      )}
    </>
  );
}
